import { useState, useCallback, useRef } from "react";
import type { GifConfig } from "../types";

// ---------------------------------------------------------------------------
// WHY TABLE INSTEAD OF HTML COMMENTS:
// Outlook's HTML processor (Word engine on desktop, browser on OWA) strips
// HTML comment nodes during setAsync/getAsync round-trips. So the previous
// <!--earthnote-gif-start--> markers disappeared after the first setAsync,
// making removal impossible — GIF stayed in body even with toggle OFF, and
// re-enabling appended a second GIF.
//
// Fix: wrap the GIF in <table class="earthnote-gif-block"> — Outlook
// preserves class attributes on table elements reliably. The imageUrl is
// used as a secondary fallback in case Outlook ever modifies the class.
//
// WHY useRef INSTEAD OF MODULE-LEVEL MAP:
// The previous Map was keyed by itemId ?? sessionKey. A new compose window
// starts with itemId=null (unsaved draft), so we used sessionKey. But after
// Outlook auto-saves, itemId becomes a real value — the key drifted and the
// registry guard broke mid-session, causing double-injection.
//
// Fix: a simple per-hook-instance boolean ref. Each task pane is its own
// JS context (separate iframe) so there is no cross-window leakage.
// ---------------------------------------------------------------------------

const GIF_MARKER_CLASS = "earthnote-gif-block";

/** Builds the HTML table block injected into the compose body.
 *  Uses a single <table> with no nested tables so the removal regex
 *  is unambiguous. */
function buildGifHtml(gif: GifConfig): string {
  return (
    `<table class="${GIF_MARKER_CLASS}" cellpadding="0" cellspacing="0" border="0" ` +
    `style="margin-top:16px;padding-top:12px;border-top:1px solid #e5e7eb;">` +
    `<tr><td style="padding:0;">` +
    `<table cellpadding="0" cellspacing="0" border="0" ` +
    `style="border-collapse:collapse;width:400px;height:100px;border:1px solid #e2e2e2;">` +
    `<tr><td style="width:400px;height:100px;text-align:center;vertical-align:middle;padding:0;overflow:hidden;">` +
    `<a href="${gif.sourceUrl}" target="_blank" rel="noopener noreferrer" style="text-decoration:none;display:block;">` +
    `<img src="${gif.imageUrl}" alt="Climate awareness GIF" ` +
    `style="display:inline-block;max-width:400px;max-height:100px;width:auto;height:auto;border:0;" />` +
    `</a>` +
    `</td></tr>` +
    `</table>` +
    `<p style="font-size:11px;color:#9ca3af;margin:4px 0 0 0;padding:0;">` +
    `<a href="${gif.sourceUrl}" target="_blank" rel="noopener noreferrer" ` +
    `style="color:#9ca3af;text-decoration:none;">${gif.sourceText}</a>` +
    `</p>` +
    `</td></tr>` +
    `</table>`
  );
}

/** Returns true if the GIF block is detectable in the HTML.
 *  Checks class attribute first, falls back to imageUrl presence. */
function isGifPresent(html: string, imageUrl: string): boolean {
  return html.includes(`class="${GIF_MARKER_CLASS}"`) || html.includes(imageUrl);
}

/** Escapes special regex characters in a string. */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

interface UseEmailBodyResult {
  injectGif: (gif: GifConfig) => Promise<void>;
  removeGif: (gif: GifConfig) => Promise<void>;
  setGifEnabled: (enabled: boolean, libraryId?: string) => Promise<void>;
  error: string | null;
}

export function useEmailBody(): UseEmailBodyResult {
  const [error, setError] = useState<string | null>(null);

  // Simple per-pane boolean — no key-drift, no cross-window issues.
  const injectedRef = useRef(false);

  // ---------------------------------------------------------------------------
  // injectGif
  // Reads body, appends GIF table if not already present. Idempotent via
  // both the ref guard and a live body scan.
  // ---------------------------------------------------------------------------
  const injectGif = useCallback(async (gif: GifConfig): Promise<void> => {
    setError(null);

    if (injectedRef.current) return;

    return new Promise<void>((resolve, reject) => {
      const item = Office.context.mailbox.item;
      if (!item) {
        resolve();
        return;
      }

      item.body.getAsync(Office.CoercionType.Html, (getResult) => {
        if (getResult.status !== Office.AsyncResultStatus.Succeeded) {
          reject(new Error(getResult.error?.message ?? "Failed to read email body"));
          return;
        }

        const currentHtml = getResult.value ?? "";

        // Live body scan handles task pane reload within the same compose window.
        if (isGifPresent(currentHtml, gif.imageUrl)) {
          injectedRef.current = true;
          resolve();
          return;
        }

        const gifBlock = buildGifHtml(gif);

        // Insert before </body> if present (OWA returns full HTML),
        // otherwise append to the end (Outlook desktop returns body fragment).
        const newHtml = currentHtml.includes("</body>")
          ? currentHtml.replace("</body>", `${gifBlock}</body>`)
          : currentHtml + gifBlock;

        item.body.setAsync(newHtml, { coercionType: Office.CoercionType.Html }, (setResult) => {
          if (setResult.status === Office.AsyncResultStatus.Succeeded) {
            injectedRef.current = true;
            resolve();
          } else {
            const msg = setResult.error?.message ?? "Failed to inject GIF into email body";
            setError(msg);
            reject(new Error(msg));
          }
        });
      });
    });
  }, []);

  // ---------------------------------------------------------------------------
  // removeGif
  // Reads body, strips the GIF table, writes cleaned body back.
  //
  // Two-pass removal strategy:
  //   Pass 1 (primary)  — class-based:
  //     <table class="earthnote-gif-block">…</table>
  //   Pass 2 (fallback) — URL-based:
  //     any <table> containing the exact imageUrl
  //     (covers cases where Outlook modifies the class attribute)
  // ---------------------------------------------------------------------------
  const removeGif = useCallback(async (gif: GifConfig): Promise<void> => {
    setError(null);

    return new Promise<void>((resolve, reject) => {
      const item = Office.context.mailbox.item;
      if (!item) {
        resolve();
        return;
      }

      item.body.getAsync(Office.CoercionType.Html, (getResult) => {
        if (getResult.status !== Office.AsyncResultStatus.Succeeded) {
          reject(new Error(getResult.error?.message ?? "Failed to read email body"));
          return;
        }

        const currentHtml = getResult.value ?? "";

        if (!isGifPresent(currentHtml, gif.imageUrl)) {
          // Body is already clean.
          injectedRef.current = false;
          resolve();
          return;
        }

        // Pass 1: class-based removal.
        let cleanedHtml = currentHtml.replace(
          new RegExp(`<table[^>]*class="${GIF_MARKER_CLASS}"[^>]*>[\\s\\S]*?<\\/table>`, "gi"),
          ""
        );

        // Pass 2: URL-based fallback (if Pass 1 changed nothing).
        if (cleanedHtml === currentHtml) {
          const escapedUrl = escapeRegex(gif.imageUrl);
          cleanedHtml = currentHtml.replace(
            new RegExp(`<table[^>]*>[\\s\\S]*?${escapedUrl}[\\s\\S]*?<\\/table>`, "gi"),
            ""
          );
        }

        item.body.setAsync(cleanedHtml, { coercionType: Office.CoercionType.Html }, (setResult) => {
          if (setResult.status === Office.AsyncResultStatus.Succeeded) {
            injectedRef.current = false;
            resolve();
          } else {
            const msg = setResult.error?.message ?? "Failed to remove GIF from email body";
            setError(msg);
            reject(new Error(msg));
          }
        });
      });
    });
  }, []);

  // ---------------------------------------------------------------------------
  // setGifEnabled
  // Sets X-EarthNote-Include header on the current compose item. When enabled
  // and a libraryId is provided, also sets X-EarthNote-Library-Id so the SMTP
  // relay can attribute usage logs / library views to the exact library that
  // appears in the compose preview.
  //
  // WHY A HEADER (instead of relying on the body marker class):
  // Outlook strips HTML comments and data-* attributes during compose
  // round-trips, but SMTP headers always survive Exchange transit. The body
  // class marker tells the relay "a GIF is already here, don't duplicate";
  // the header additionally tells the relay "this is the specific library
  // that's there" — needed because the rotation pool returns random gifs
  // each call, and the relay's fresh fetch would otherwise log against the
  // wrong library.
  // ---------------------------------------------------------------------------
  const setGifEnabled = useCallback(async (enabled: boolean, libraryId?: string): Promise<void> => {
    setError(null);
    return new Promise<void>((resolve, reject) => {
      const headers: Record<string, string> = {
        "X-EarthNote-Include": enabled ? "true" : "false",
      };
      if (enabled && libraryId) {
        headers["X-EarthNote-Library-Id"] = libraryId;
      }
      Office.context.mailbox.item?.internetHeaders.setAsync(headers, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve();
        } else {
          const msg = result.error?.message ?? "Failed to update email header";
          setError(msg);
          reject(new Error(msg));
        }
      });
    });
  }, []);

  return { injectGif, removeGif, setGifEnabled, error };
}
